// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    Heading,
    List,
    Translate,
} from '@plesk/ui-library';
import { useMemo } from 'react';
import { StyledTable } from 'common/components/styles/StyledTable';
import { IComputeResourceRecoveryReadinessStatusResponse } from 'common/api/resources/DisasterRecovery';
import { ListColumn } from '@plesk/ui-library/types/components/List';
import CheckCrossIcon from 'admin/disasterRecovery/components/CheckCrossIcon/CheckCrossIcon';

export default function BackupCoverageTable({
    backupCoverage,
}: {
    backupCoverage?: IComputeResourceRecoveryReadinessStatusResponse['backup_coverage'];
}) {

    const columns = useMemo((): Array<ListColumn<{
        total: string | number;
        available: string | number;
    }>> => [
        {
            key: 'title',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.category" />,
        },
        {
            key: 'status',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.status" />,
            align: 'center',
            render: (row) => <CheckCrossIcon value={row.total === row.available} />,
        },
        {
            key: 'available',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.available" />,
            align: 'right',
        },
        {
            key: 'expired',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.expired" />,
            align: 'right',
        },
        {
            key: 'total',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.total" />,
            align: 'right',
        },
        {
            key: 'withoutBackups',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.withoutBackups" />,
            align: 'right',
        },
    ], []);

    const data = useMemo(() => [
        {
            key: 'all',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.all" />,
            available: backupCoverage?.all.available_backups.toLocaleString() ?? 0,
            expired: backupCoverage?.all.expired_backups.toLocaleString() ?? 0,
            total: backupCoverage?.all.total.toLocaleString() ?? 0,
            withoutBackups: backupCoverage?.all.without_backups.toLocaleString() ?? 0,
        },
        {
            key: 'withDR',
            title: <Translate content="disasterRecovery.readinessStatus.backupCoverage.table.withDR" />,
            available: backupCoverage?.with_disaster_recovery_enabled.available_backups.toLocaleString() ?? 0,
            expired: backupCoverage?.with_disaster_recovery_enabled.expired_backups.toLocaleString() ?? 0,
            total: backupCoverage?.with_disaster_recovery_enabled.total.toLocaleString() ?? 0,
            withoutBackups: backupCoverage?.with_disaster_recovery_enabled.without_backups.toLocaleString() ?? 0,
        },
    ], [backupCoverage]);

    return (
        <div style={{ marginBottom: 16 }}>
            <Heading level={3}>
                <Translate content="disasterRecovery.readinessStatus.backupCoverage.title" />
            </Heading>

            <StyledTable>
                <List
                    columns={columns}
                    data={data}
                    rowKey={'key'}
                />
            </StyledTable>
        </div>
    );
}